import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { 
  User, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { cleanupOnLogout } from '../utils/localStorageCleanup';
import { activity } from '../utils/activityLogger';

interface UserData {
  name?: string;
  lastname?: string;
  user_type?: string;
  email?: string;
  assigned_clients?: string[];
  preferred_language?: string;
}

interface AuthContextType {
  currentUser: User | null;
  userData: UserData | null;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);

  const signup = useCallback(async (email: string, password: string) => {
    await createUserWithEmailAndPassword(auth, email, password);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    // Clear manual language change flag on new login
    localStorage.removeItem('userManuallyChangedLang');
    await signInWithEmailAndPassword(auth, email, password);
    // Activity tracking (GH #21 / #82): registrar login real. Fire-and-forget,
    // nunca rompe el flujo de auth si el POST falla.
    activity.login();
  }, []);

  const logout = useCallback(async () => {
    // Clean up localStorage before signing out
    cleanupOnLogout();
    // Clear only the manual language change flag, not the language itself
    // This prevents the UI from flickering back to default language
    localStorage.removeItem('userManuallyChangedLang');
    await signOut(auth);
    setUserData(null); // Limpiar datos del usuario al cerrar sesión
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  }, []);

  // Función para cargar datos del usuario desde Firestore
  const loadUserData = async (user: User) => {
    if (!user) {
      setUserData(null);
      return;
    }

    try {
      // Buscar en la colección 'users' usando el UID del usuario autenticado
      const userDocRef = doc(db, 'users', user.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (userDocSnap.exists()) {
        const firestoreData = userDocSnap.data();
        const preferredLanguage = firestoreData.preferred_language || 'es';

        // Compatibilidad: usar assigned_clients si existe, sino clients
        const assignedClients = firestoreData.assigned_clients || firestoreData.clients || [];

        setUserData({
          name: firestoreData.name || '',
          lastname: firestoreData.lastname || '',
          user_type: firestoreData.user_type || 'user',
          email: user.email || '',
          assigned_clients: assignedClients,
          preferred_language: preferredLanguage
        });
      } else {
        // Si no existe el documento, usar valores por defecto
        setUserData({
          name: user.email?.split('@')[0] || 'Usuario',
          user_type: 'user',
          email: user.email || '',
          preferred_language: 'es'
        });
      }
    } catch (error) {
      // Solo mostrar error si no es un problema conocido de permisos
      if (error instanceof Error && !error.message.includes('Missing or insufficient permissions')) {
        console.error('[AuthContext] Error loading user data:', error);
      }

      // En caso de error, usar valores por defecto
      setUserData({
        name: user.email?.split('@')[0] || 'Usuario',
        user_type: 'user',
        email: user.email || '',
        preferred_language: 'es'
      });
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        setCurrentUser(user);
        
        if (user) {
          await loadUserData(user);
        } else {
          setUserData(null);
        }
      } catch (error) {
        console.error('Error in auth state change:', error);
        // En caso de error, seguir con valores por defecto
        setUserData(null);
      } finally {
        setLoading(false);
      }
    }, (error) => {
      // Manejo de errores del onAuthStateChanged
      console.error('Auth state change error:', error);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = useMemo<AuthContextType>(() => ({
    currentUser,
    userData,
    login,
    signup,
    logout,
    resetPassword,
    loading
  }), [currentUser, userData, login, signup, logout, resetPassword, loading]);

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 